import React, { useMemo } from 'react';
import { Alert, Flex, Progress, Table, Tag, Typography } from 'antd';
import {
    computeParameterRelevance,
    getRelevanceLevelLabel,
    type ParameterRelevance,
} from '../utils/relevanceAnalysis';

const { Text, Paragraph } = Typography;

const nivelTagColor = (nivel: ParameterRelevance['nivel']) => {
    switch (nivel) {
        case 'alta':
            return 'green';
        case 'media':
            return 'gold';
        default:
            return 'default';
    }
};

const progressStrokeColor = (peso: number) => {
    if (peso >= 70) {
        return '#52c41a';
    }
    if (peso >= 40) {
        return '#faad14';
    }
    return '#d9d9d9';
};

interface RelevanciaInformacoesProps {
    resultAnalise: any;
}

const RelevanciaInformacoes = ({ resultAnalise }: RelevanciaInformacoesProps) => {
    const relevancias = useMemo(
        () => computeParameterRelevance(resultAnalise),
        [resultAnalise]
    );

    const resumo = useMemo(() => {
        const relevantes = relevancias.filter((item) => item.relevante).length;
        return {
            total: relevancias.length,
            relevantes,
            alta: relevancias.filter((item) => item.nivel === 'alta').length,
        };
    }, [relevancias]);

    if (!relevancias.length) {
        return (
            <Alert
                type="info"
                showIcon
                message="Não há dados suficientes para calcular a relevância estatística dos parâmetros."
            />
        );
    }

    const columns = [
        {
            title: 'Parâmetro',
            dataIndex: 'parametro',
            key: 'parametro',
            width: '16%',
        },
        {
            title: 'Categoria',
            dataIndex: 'categoria',
            key: 'categoria',
            width: '10%',
            render: (value: ParameterRelevance['categoria']) =>
                value === 'numeros' ? 'Números' : 'Sorteios',
        },
        {
            title: 'Peso de relevância',
            dataIndex: 'peso',
            key: 'peso',
            width: '18%',
            sorter: (a: ParameterRelevance, b: ParameterRelevance) => a.peso - b.peso,
            defaultSortOrder: 'descend' as const,
            render: (peso: number) => (
                <Flex vertical gap={4}>
                    <Progress
                        percent={peso}
                        size="small"
                        strokeColor={progressStrokeColor(peso)}
                        format={(percent) => `${percent}%`}
                    />
                </Flex>
            ),
        },
        {
            title: 'Relevante',
            dataIndex: 'relevante',
            key: 'relevante',
            width: '10%',
            filters: [
                { text: 'Sim', value: true },
                { text: 'Não', value: false },
            ],
            onFilter: (value: boolean, record: ParameterRelevance) => record.relevante === value,
            render: (relevante: boolean, record: ParameterRelevance) => (
                <Tag color={relevante ? 'success' : 'default'}>
                    {relevante ? 'Sim' : 'Não'}
                </Tag>
            ),
        },
        {
            title: 'Nível',
            dataIndex: 'nivel',
            key: 'nivel',
            width: '8%',
            render: (nivel: ParameterRelevance['nivel']) => (
                <Tag color={nivelTagColor(nivel)}>{getRelevanceLevelLabel(nivel)}</Tag>
            ),
        },
        {
            title: 'Interpretação',
            dataIndex: 'interpretacao',
            key: 'interpretacao',
            width: '38%',
            render: (text: string) => <Text type="secondary">{text}</Text>,
        },
    ];

    return (
        <Flex vertical gap="middle" style={{ width: '100%' }}>
            <Alert
                type="info"
                showIcon
                message="Como interpretar"
                description={
                    <Paragraph style={{ marginBottom: 0 }}>
                        O peso combina entropia de Shannon (concentração da distribuição),
                        qui-quadrado contra distribuição uniforme e coeficiente de variação.
                        Parâmetros com peso ≥ 40% são considerados relevantes para orientar
                        filtros e estratégias com base no histórico analisado.
                    </Paragraph>
                }
            />

            <Flex wrap gap="small">
                <Tag color="blue">{resumo.total} parâmetros analisados</Tag>
                <Tag color="green">{resumo.relevantes} relevantes</Tag>
                <Tag color="cyan">{resumo.alta} com relevância alta</Tag>
            </Flex>

            <Table
                style={{ width: '100%' }}
                bordered
                rowKey="id"
                size="small"
                pagination={false}
                columns={columns}
                dataSource={relevancias}
                expandable={{
                    expandedRowRender: (record) => (
                        <Flex vertical gap={4}>
                            <Text>
                                Entropia normalizada: {(record.metricas.entropiaNormalizada * 100).toFixed(1)}%
                            </Text>
                            <Text>Qui-quadrado (χ²): {record.metricas.quiQuadrado.toFixed(2)}</Text>
                            <Text>
                                Coeficiente de variação: {record.metricas.coeficienteVariacao.toFixed(3)}
                            </Text>
                        </Flex>
                    ),
                }}
            />
        </Flex>
    );
};

export default RelevanciaInformacoes;
